import {useIntl} from 'react-intl'
import {formatPersonalizedMessage} from '../../utils/form-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const NAME_REGEX = /^[A-Za-zÀ-ž\s,.\-']+$/
const MESSAGE_REGEX = /^[A-Za-z0-9\s.,!?'"()-]*$/
const {
    app: {commerceAPI}
} = getConfig()

const {eGiftCards} = commerceAPI.storefront

let PROFANITY = []

try {
    const parsed = JSON.parse(eGiftCards || '{}')

    const words = parsed.restrictedWords
    PROFANITY = Array.isArray(words) ? words.map((w) => String(w).trim()) : []
} catch (err) {
    console.warn('Failed to parse restrictedWords:', err)
    PROFANITY = []
}

export default function useGiftcardFields({
    form: {
        control,
        setValue,
        clearErrors,
        formState: {errors},
        getValues,
        register
    },
    prefix = '',
    setCharactersRemaining
}) {
    const backgroundColor = 'white'
    const {formatMessage} = useIntl()
    const MAX_MESSAGE_LENGTH = 200

    const fromFirstName = {
        name: `${prefix}fromFirstName`,
        label: formatMessage({id: 'eGiftcard.firstName', defaultMessage: 'First Name'}),
        placeholder: 'First Name',
        defaultValue: '',
        type: 'text',
        register,
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            required: formatMessage({
                id: 'form.field.required',
                defaultMessage: 'This field is required.'
            }),
            pattern: {
                value: NAME_REGEX,
                message: formatMessage({
                    defaultMessage:
                        'Please enter only letters, no numbers or symbols allowed in this field.',
                    id: 'form.enter.valid.name'
                })
            }
        },
        error: errors?.[`${prefix}fromFirstName`],
        onChange: (e) => {
            const input = e.target.value
            if (!NAME_REGEX.test(input) && input !== '') {
                e.preventDefault()
                return
            }
            setValue(`${prefix}fromFirstName`, input, {shouldValidate: true})
        }
    }

    const fromLastName = {
        name: `${prefix}fromLastName`,
        label: formatMessage({id: 'eGiftcard.lastName', defaultMessage: 'Last Name'}),
        placeholder: 'Last Name',
        defaultValue: '',
        type: 'text',
        register,
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            required: formatMessage({
                defaultMessage: 'This field is required.',
                id: 'form.field.required'
            }),
            pattern: {
                value: NAME_REGEX,
                message: formatMessage({
                    defaultMessage:
                        'Please enter only letters, no numbers or symbols allowed in this field.',
                    id: 'form.enter.valid.name'
                })
            }
        },
        error: errors?.[`${prefix}fromLastName`],
        onChange: (e) => {
            const input = e.target.value
            if (!NAME_REGEX.test(input) && input !== '') {
                e.preventDefault()
                return
            }
            setValue(`${prefix}fromLastName`, input, {shouldValidate: true})
        }
    }

    const toFirstName = {
        name: `${prefix}toFirstName`,
        label: formatMessage({
            defaultMessage: 'First Name',
            id: 'eGiftcard.firstName'
        }),
        placeholder: 'First Name',
        defaultValue: '',
        type: 'text',
        register,
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            required: formatMessage({
                defaultMessage: 'This field is required.',
                id: 'form.field.required'
            }),
            pattern: {
                value: NAME_REGEX,
                message: formatMessage({
                    defaultMessage:
                        'Please enter only letters, no numbers or symbols allowed in this field.',
                    id: 'form.enter.valid.name'
                })
            }
        },
        error: errors?.[`${prefix}toFirstName`],
        onChange: (e) => {
            const input = e.target.value
            if (!NAME_REGEX.test(input) && input !== '') {
                e.preventDefault()
                return
            }
            setValue(`${prefix}toFirstName`, input, {shouldValidate: true})
        }
    }

    const toLastName = {
        name: `${prefix}toLastName`,
        label: formatMessage({
            defaultMessage: 'Last Name',
            id: 'eGiftcard.lastName'
        }),
        placeholder: 'Last Name',
        defaultValue: '',
        type: 'text',
        register,
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            required: formatMessage({
                defaultMessage: 'This field is required.',
                id: 'form.field.required'
            }),
            pattern: {
                value: NAME_REGEX,
                message: formatMessage({
                    defaultMessage:
                        'Please enter only letters, no numbers or symbols allowed in this field.',
                    id: 'form.enter.valid.name'
                })
            }
        },
        error: errors?.[`${prefix}toLastName`],
        onChange: (e) => {
            const input = e.target.value
            if (!NAME_REGEX.test(input) && input !== '') {
                e.preventDefault()
                return
            }
            setValue(`${prefix}toLastName`, input, {shouldValidate: true})
        }
    }

    const email = {
        name: `${prefix}email`,
        label: formatMessage({defaultMessage: 'Email', id: 'use_login_fields.label.email'}),
        placeholder: 'Email',
        defaultValue: '',
        type: 'text',
        autoComplete: 'email',
        register,
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            required: formatMessage({
                defaultMessage: 'This field is required.',
                id: 'form.field.required'
            }),
            pattern: {
                value: /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/,
                message: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'form.enter.valid.email'
                })
            }
        },
        error: errors?.[`${prefix}email`]
    }

    const confirmEmail = {
        name: `${prefix}confirmEmail`,
        label: formatMessage({
            defaultMessage: 'Confirm Email',
            id: 'use_login_fields.label.confirmEmail'
        }),
        placeholder: 'Email',
        defaultValue: '',
        type: 'text',
        autoComplete: 'email',
        register,
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            required: formatMessage({
                defaultMessage: 'This field is required.',
                id: 'form.field.required'
            }),
            validate: (value) => {
                const trimmed = value.trim()
                const original = (getValues(`${prefix}email`) || '').trim()
                if (!trimmed)
                    return formatMessage({
                        id: 'form.field.required',
                        defaultMessage: 'This field is required.'
                    })
                if (trimmed.toLowerCase() !== original.toLowerCase()) {
                    return formatMessage({
                        id: 'form.confirm.email.mismatch',
                        defaultMessage:
                            'Please check that the e-mail addresses match and try again.'
                    })
                }
                return true
            }
        },
        error: errors?.[`${prefix}confirmEmail`]
    }

    const personalizedMessage = {
        name: `${prefix}personalizedMessage`,
        label: formatMessage({
            defaultMessage: 'Optional Message',
            id: 'eGiftcard.personalized.message'
        }),
        placeholder: 'Optional Message',
        inputProps: ({onChange}) => ({
            onChange(evt) {
                const val = formatPersonalizedMessage(evt.target.value).slice(0, MAX_MESSAGE_LENGTH)
                setCharactersRemaining(MAX_MESSAGE_LENGTH - val.length)
                setValue(`${prefix}personalizedMessage`, val, {shouldValidate: true})
                onChange(val)
            }
        }),
        defaultValue: '',
        type: 'textarea',
        control,
        setValue,
        clearErrors,
        backgroundColor,
        rules: {
            validate: (value) => {
                const v = value || ''
                const lower = v.toLowerCase()
                if (!MESSAGE_REGEX.test(v))
                    return formatMessage({
                        id: 'form.enter.valid.message',
                        defaultMessage: 'Please enter only valid characters.'
                    })
                // Split into words using whitespace + punctuation
                const words = lower.split(/[\s.,!?;:()"'[\]{}<>/\\|-]+/)
                if (PROFANITY?.some((bad) => words.includes(bad.toLowerCase()))) {
                    return formatMessage({
                        id: 'form.enter.valid.message',
                        defaultMessage: 'Please enter only valid characters.'
                    })
                }
                return true
            }
        },
        error: errors?.[`${prefix}personalizedMessage`]
    }

    return {
        fromFirstName,
        fromLastName,
        toFirstName,
        toLastName,
        email,
        confirmEmail,
        personalizedMessage
    }
}
